import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { 
  LayoutGrid, 
  Users, 
  Trophy, 
  Calendar, 
  Medal, 
  Table, 
  PiggyBank, 
  Gavel,
  Crown,
  PenTool,
  GripVertical 
} from 'lucide-react';

// Icon mapping
const ICONS = {
  Users,
  Trophy,
  Calendar,
  Medal,
  Table,
  PiggyBank,
  Gavel,
  Crown,
  PenTool
};

// Optional pages configuration
const OPTIONAL_PAGES = [
  { id: 'players', label: 'Players', icon: 'Users', description: 'Manage team roster and player stats' },
  { id: 'matches', label: 'Matches', icon: 'Trophy', description: 'Track matches, lineups and results' },
  { id: 'training', label: 'Training', icon: 'Calendar', description: 'Schedule sessions and track attendance' },
  { id: 'leaderboard', label: 'Leaderboard', icon: 'Medal', description: 'View player rankings and stats' },
  { id: 'league-table', label: 'League Table', icon: 'Table', description: 'Track league standings and positions' },
  { id: 'team-kitty', label: 'Team Kitty', icon: 'PiggyBank', description: 'Track team finances' },
  { id: 'fines', label: 'Fines', icon: 'Gavel', description: 'Track player fines and payments' },
  { id: 'coaches-office', label: "Coach's Office", icon: 'Crown', description: 'Coach management tools and resources' },
  { id: 'tactics-board', label: 'Tactics Board', icon: 'PenTool', description: 'Design and save tactical plays and formations' },
];

export const NavigationSection = ({
  pageOrder,
  setPageOrder,
  hiddenPages,
  togglePageVisibility,
  draggedPage,
  setDraggedPage,
  dragOverPage,
  setDragOverPage
}) => {
  const handlePageDragStart = (e, pageId) => {
    setDraggedPage(pageId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePageDragOver = (e, pageId) => {
    e.preventDefault();
    if (pageId !== draggedPage) {
      setDragOverPage(pageId);
    }
  };

  const handlePageDragLeave = () => {
    setDragOverPage(null);
  };

  const handlePageDrop = (e, targetPageId) => {
    e.preventDefault();
    if (draggedPage && draggedPage !== targetPageId) {
      const newOrder = [...pageOrder];
      const draggedIndex = newOrder.indexOf(draggedPage);
      const targetIndex = newOrder.indexOf(targetPageId);
      
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedPage);
      
      setPageOrder(newOrder);
    }
    setDraggedPage(null);
    setDragOverPage(null);
  };

  const handlePageDragEnd = () => {
    setDraggedPage(null);
    setDragOverPage(null);
  };

  return (
    <Card data-testid="navigation-card">
      <CardHeader>
        <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          Navigation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500">
          Choose which pages appear in your navigation menu and drag to reorder. Dashboard, Seasons, and Settings are always visible.
        </p>
        
        <div className="text-xs text-gray-400 flex items-center gap-2 pb-2">
          <GripVertical className="w-4 h-4" strokeWidth={1.5} />
          <span>Drag to reorder</span>
        </div>
        
        <div className="flex flex-col gap-4">
          {pageOrder.map((pageId) => {
            const page = OPTIONAL_PAGES.find(p => p.id === pageId);
            if (!page) return null;
            
            const Icon = ICONS[page.icon];
            const isEnabled = !hiddenPages.includes(page.id);
            const isDragging = draggedPage === page.id;
            const isDragOver = dragOverPage === page.id;
            
            return (
              <div 
                key={page.id}
                draggable
                onDragStart={(e) => handlePageDragStart(e, page.id)}
                onDragOver={(e) => handlePageDragOver(e, page.id)}
                onDragLeave={handlePageDragLeave}
                onDrop={(e) => handlePageDrop(e, page.id)}
                onDragEnd={handlePageDragEnd}
                className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-move ${
                  isEnabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'
                } ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
              >
                <div className="flex items-center gap-4">
                  <div className="text-gray-400 cursor-grab active:cursor-grabbing">
                    <GripVertical className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isEnabled ? 'bg-emerald-100' : 'bg-gray-200'
                  }`}>
                    {Icon && <Icon className={`w-5 h-5 ${isEnabled ? 'text-emerald-600' : 'text-gray-400'}`} strokeWidth={1.5} />}
                  </div>
                  <div>
                    <p className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}>
                      {page.label}
                    </p>
                    <p className="text-sm text-gray-500">{page.description}</p>
                  </div>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={() => togglePageVisibility(page.id)}
                  data-testid={`toggle-page-${page.id}`}
                />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default NavigationSection;
